import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import moment from "moment";
import { collection, getDocs, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { DATABASE, FUNCTIONS } from "../../../../Config/firebase";

const fieldLabelSx = {
  fontSize: "12px",
  fontWeight: 500,
  color: "#6b7280",
  letterSpacing: "0.02em",
  textTransform: "uppercase",
  fontFamily: "Poppins",
  mb: 0.35,
};

const STEPS = ["Recipient", "Amount & settlement", "Compliance", "Confirm"];

/** Internal QA only — longer windows use Production (days). */
const TEST_MAX_MINUTES = 5;
const TEST_MINUTE_PRESETS = [1, 2, 3, 4, 5];
/** 1 = next calendar day at ~same time (common for client routings). */
const PROD_DAY_PRESETS = [1, 2, 3, 5];

const productionPresetLabel = (days) => {
  if (days === 1) return "1 d · next day";
  return `${days} d`;
};

const parsePositiveInt = (raw, fallback) => {
  const n = parseInt(String(raw).trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const computeSettlePreview = ({ testMode, holdingMinutes, holdingDays }) => {
  const now = Date.now();
  let ms;
  if (testMode) {
    const mins = Math.max(1, parsePositiveInt(holdingMinutes, 5));
    ms = now + mins * 60 * 1000;
  } else {
    const days = Math.max(1, parsePositiveInt(holdingDays, 1));
    ms = now + days * 24 * 60 * 60 * 1000;
  }
  const d = new Date(ms);
  return {
    utc: moment(d).utc().format("DD MMM YYYY, HH:mm [UTC]"),
    local: moment(d).format("DD MMM YYYY, HH:mm [(local)]"),
  };
};

const settlementSummary = ({ testMode, holdingMinutes, holdingDays }) => {
  if (testMode) {
    const mins = parsePositiveInt(holdingMinutes, 5);
    return `${mins} minute${mins === 1 ? "" : "s"} (test)`;
  }
  const days = parsePositiveInt(holdingDays, 1);
  return `${days} day${days === 1 ? "" : "s"} (production window)`;
};

const holdingInputValid = ({ testMode, holdingMinutes, holdingDays }) => {
  if (testMode) {
    const mins = parsePositiveInt(holdingMinutes, NaN);
    return Number.isFinite(mins) && mins >= 1 && mins <= TEST_MAX_MINUTES;
  }
  const days = parsePositiveInt(holdingDays, NaN);
  return Number.isFinite(days) && days >= 1 && days <= 14;
};

/** MUI Dialog z-index 1300; SweetAlert2 must sit above it. */
const swalAboveDialog = (options) =>
  Swal.fire({
    ...options,
    didOpen: (popup) => {
      const container = popup?.parentElement || document.querySelector(".swal2-container");
      if (container) container.style.zIndex = "1400";
      if (typeof options.didOpen === "function") options.didOpen(popup);
    },
  });

const getCallableErrorMessage = (err) => {
  if (!err) return "Failed to initiate routing.";
  if (typeof err?.message === "string" && err.message.length > 0) return err.message;
  if (typeof err?.details === "string") return err.details;
  if (err?.code) return err.code;
  return "Failed to initiate routing.";
};

const CapitalRoutingWizard = ({ open, onClose, sender, onCompleted }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [recipient, setRecipient] = useState(null);
  const [approvedAccounts, setApprovedAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [amount, setAmount] = useState("");
  const [testMode, setTestMode] = useState(false);
  const [holdingDays, setHoldingDays] = useState("1");
  const [holdingMinutes, setHoldingMinutes] = useState("5");
  const [clientEmailRef, setClientEmailRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [attestations, setAttestations] = useState({
    kycVerified: false,
    fundsOriginConfirmed: false,
    clientRequestOnFile: false,
    manualReviewComplete: false,
  });

  const senderId = sender?.doc_id || sender?.user_id || sender?.id;
  const senderBalance = Number(sender?.net_balance) || 0;

  const recipientOptions = useMemo(() => {
    const filtered = approvedAccounts.filter(
      (u) => u.doc_id !== senderId && u.account_operations_frozen !== true
    );
    return filtered.sort((a, b) => {
      const labelA = (a.full_name || a.name || a.email || "").trim().toLowerCase();
      const labelB = (b.full_name || b.name || b.email || "").trim().toLowerCase();
      return labelA.localeCompare(labelB, "en", { sensitivity: "base" });
    });
  }, [approvedAccounts, senderId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const loadApprovedAccounts = async () => {
      setLoadingAccounts(true);
      try {
        const usersQuery = query(
          collection(DATABASE, "users"),
          where("role", "==", 1),
          where("status", "==", 1)
        );
        const snap = await getDocs(usersQuery);
        if (cancelled) return;
        const users = snap.docs.map((d) => ({
          ...d.data(),
          doc_id: d.id,
        }));
        setApprovedAccounts(users);
      } catch (err) {
        console.error(err);
        if (!cancelled) toast.error("Failed to load approved accounts.");
      } finally {
        if (!cancelled) setLoadingAccounts(false);
      }
    };
    loadApprovedAccounts();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const resetForm = () => {
    setActiveStep(0);
    setRecipient(null);
    setAmount("");
    setTestMode(false);
    setHoldingDays("1");
    setHoldingMinutes("5");
    setClientEmailRef("");
    setAttestations({
      kycVerified: false,
      fundsOriginConfirmed: false,
      clientRequestOnFile: false,
      manualReviewComplete: false,
    });
  };

  const handleClose = () => {
    if (busy) return;
    resetForm();
    onClose?.();
  };

  const parsedAmount = useMemo(() => parseFloat(String(amount).replace(/,/g, "")), [amount]);

  const settlePreview = useMemo(
    () => computeSettlePreview({ testMode, holdingMinutes, holdingDays }),
    [testMode, holdingMinutes, holdingDays]
  );

  const settlementLabel = useMemo(
    () => settlementSummary({ testMode, holdingMinutes, holdingDays }),
    [testMode, holdingMinutes, holdingDays]
  );

  const getRecipientLabel = (option) => {
    if (!option) return "";
    const name = option.full_name || option.name || "Client";
    const email = option.email || "";
    const balance = Number(option.net_balance);
    const balanceStr = Number.isFinite(balance)
      ? ` · $${balance.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
      : "";
    return `${name} (${email})${balanceStr}`;
  };

  const allAttestationsChecked = Object.values(attestations).every(Boolean);

  const canNext = () => {
    if (activeStep === 0) return Boolean(recipient?.doc_id);
    if (activeStep === 1) {
      return (
        parsedAmount > 0 &&
        parsedAmount <= senderBalance &&
        holdingInputValid({ testMode, holdingMinutes, holdingDays })
      );
    }
    if (activeStep === 2) return allAttestationsChecked;
    return true;
  };

  const handleNext = () => {
    if (!canNext()) {
      toast.warning("Complete the current step before continuing.");
      return;
    }
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    if (!senderId || !recipient?.doc_id) {
      toast.error("Missing sender or recipient. Close the wizard and try again.");
      return;
    }
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      toast.error("Enter a valid amount.");
      return;
    }

    const confirm = await swalAboveDialog({
      title: "Execute Capital Routing?",
      html: `Debit <b>$${parsedAmount.toFixed(2)}</b> from <b>${sender?.email}</b><br/>Credit to <b>${recipient?.email}</b> after settlement.<br/><br/>
        <b>Settlement window:</b> ${settlementLabel}<br/>
        <b>Scheduled credit (approx.):</b><br/>${settlePreview.utc}<br/>${settlePreview.local}<br/><br/>
        This is a compliance-controlled operation.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Execute routing",
      confirmButtonColor: "#ed6c02",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    setBusy(true);
    try {
      const fn = httpsCallable(FUNCTIONS, "initiateRouting");
      const { data } = await fn({
        fromUserId: senderId,
        toUserId: recipient.doc_id,
        amount: parsedAmount,
        testMode,
        holdingDays: testMode ? undefined : Number(holdingDays) || 1,
        holdingMinutes: testMode ? Number(holdingMinutes) || 5 : undefined,
        clientEmailRef: clientEmailRef.trim(),
        complianceAttestations: attestations,
      });
      toast.success(`Routing initiated (${data?.routingId || "ok"}). Settles: ${data?.settleAt || "scheduled"}`);
      onCompleted?.();
      handleClose();
    } catch (err) {
      console.error("initiateRouting:", err);
      toast.error(getCallableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontFamily: "Poppins", fontWeight: 600 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SwapHorizIcon color="warning" />
          Capital Routing
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: "#6b7280", mb: 2, fontFamily: "Poppins" }}>
          Internal capital routing between Fiddux accounts. Manual compliance review required. Not available to clients in the app.
        </Typography>
        <Box sx={{ mb: 2, p: 2, bgcolor: "#f9fafb", borderRadius: 1 }}>
          <Typography sx={fieldLabelSx}>Sender</Typography>
          <Typography sx={{ fontFamily: "Poppins", fontWeight: 600 }}>
            {sender?.full_name || "—"} · {sender?.email}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#374151", mt: 0.5 }}>
            Available balance: ${senderBalance.toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </Typography>
        </Box>

        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={recipientOptions}
                loading={loadingAccounts}
                value={recipient}
                onChange={(_event, newValue) => setRecipient(newValue)}
                getOptionLabel={getRecipientLabel}
                isOptionEqualToValue={(option, value) => option.doc_id === value?.doc_id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Recipient account"
                    placeholder="Search approved client…"
                    size="small"
                    helperText="Only approved customer accounts (status Approved). Frozen accounts are excluded."
                  />
                )}
                noOptionsText={
                  loadingAccounts ? "Loading accounts…" : "No approved accounts match your search"
                }
              />
            </Grid>
          </Grid>
        )}

        {activeStep === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Amount (USDT)"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ ...fieldLabelSx, mb: 1 }}>Settlement window</Typography>
              <ToggleButtonGroup
                value={testMode ? "test" : "production"}
                exclusive
                size="small"
                onChange={(_e, value) => {
                  if (value === "test") setTestMode(true);
                  if (value === "production") {
                    setTestMode(false);
                    setHoldingDays("1");
                  }
                }}
                sx={{ mb: 1.5, flexWrap: "wrap" }}
              >
                <ToggleButton value="test" sx={{ textTransform: "none" }}>
                  Test (minutes)
                </ToggleButton>
                <ToggleButton value="production" sx={{ textTransform: "none" }}>
                  Production (days)
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 1, fontFamily: "Poppins" }}>
                {testMode
                  ? `Internal QA only (${TEST_MINUTE_PRESETS[0]}–${TEST_MAX_MINUTES} minutes). Use Production for real client routings.`
                  : "Production window (calendar days). Client e-mails still mention 2–3 business days; earlier credit is fine."}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: 12, color: "#374151", mb: 0.75, fontFamily: "Poppins" }}>
                Presets
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={
                  testMode
                    ? String(parsePositiveInt(holdingMinutes, 5))
                    : String(parsePositiveInt(holdingDays, 1))
                }
                onChange={(_e, value) => {
                  if (!value) return;
                  if (testMode) setHoldingMinutes(value);
                  else setHoldingDays(value);
                }}
                sx={{ flexWrap: "wrap", gap: 0.5 }}
              >
                {(testMode ? TEST_MINUTE_PRESETS : PROD_DAY_PRESETS).map((n) => (
                  <ToggleButton
                    key={n}
                    value={String(n)}
                    sx={{ textTransform: "none", minWidth: testMode ? 48 : undefined, px: testMode ? 1 : 1.5 }}
                  >
                    {testMode ? `${n} min` : productionPresetLabel(n)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={testMode ? "Custom settlement (minutes)" : "Custom settlement (days)"}
                value={testMode ? holdingMinutes : holdingDays}
                onChange={(e) => (testMode ? setHoldingMinutes(e.target.value) : setHoldingDays(e.target.value))}
                size="small"
                type="number"
                inputProps={{
                  min: 1,
                  max: testMode ? TEST_MAX_MINUTES : 14,
                }}
                helperText={
                  testMode
                    ? `1–${TEST_MAX_MINUTES} minutes (QA). Longer → switch to Production (days).`
                    : "1–14 calendar days (1 day ≈ credit tomorrow at this time)"
                }
                error={!holdingInputValid({ testMode, holdingMinutes, holdingDays })}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 1.5, bgcolor: "#fffbeb", borderRadius: 1, border: "1px solid #fcd34d" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#92400e", fontFamily: "Poppins" }}>
                  Scheduled recipient credit (if you submit now)
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#78350f", mt: 0.5, fontFamily: "Poppins" }}>
                  {settlePreview.utc}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#78350f", fontFamily: "Poppins" }}>
                  {settlePreview.local}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}

        {activeStep === 2 && (
          <Box>
            <TextField
              fullWidth
              label="Client email reference (optional)"
              value={clientEmailRef}
              onChange={(e) => setClientEmailRef(e.target.value)}
              size="small"
              sx={{ mb: 2 }}
                    helperText="Internal reference only — stored for audit; does not send email to this address."
            />
            <Divider sx={{ my: 1 }} />
            {[
              ["kycVerified", "KYC verified for both sender and recipient"],
              ["fundsOriginConfirmed", "Origin of funds confirmed"],
              ["clientRequestOnFile", "Complete client routing request on file"],
              ["manualReviewComplete", "Manual compliance review completed"],
            ].map(([key, label]) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={attestations[key]}
                    onChange={(e) => setAttestations((a) => ({ ...a, [key]: e.target.checked }))}
                  />
                }
                label={label}
              />
            ))}
          </Box>
        )}

        {activeStep === 3 && (
          <Box sx={{ fontFamily: "Poppins", fontSize: 14, lineHeight: 1.6 }}>
            <p><strong>From:</strong> {sender?.email}</p>
            <p><strong>To:</strong> {recipient?.email}</p>
            <p><strong>Amount:</strong> ${parsedAmount.toFixed(2)}</p>
            <p><strong>Settlement window:</strong> {settlementLabel}</p>
            <p><strong>Scheduled credit (approx.):</strong> {settlePreview.utc} · {settlePreview.local}</p>
            <Typography sx={{ fontSize: 11, color: "#9ca3af", mt: 2 }}>
              Settlement may take 2-3 business days; account performance during the process may be subject to differentiation.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={busy}>Cancel</Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={busy}>Back</Button>
        )}
        {activeStep < STEPS.length - 1 ? (
          <Button variant="contained" onClick={handleNext} disabled={busy || !canNext()}>
            Next
          </Button>
        ) : (
          <Button variant="contained" color="warning" onClick={handleSubmit} disabled={busy || !allAttestationsChecked}>
            {busy ? "Processing…" : "Execute routing"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CapitalRoutingWizard;
